import { usePricingHeroData } from '../../../../modules/apis/sanity'
import { useCustomerType } from '../../../../modules/apis/student/products'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { buildSanityImage, isWithIn24HoursUser } from '../../../../modules/utils'

const USER_TYPE = {
  B2C: 'b2c',
  BUSINESS: 'business',
} as const

const B2C_TYPE = {
  GUEST: 'guest',
  WITHIN_24: 'within24',
  AFTER_24: 'after24',
} as const

const BUSINESS_TYPE = {
  GUEST: 'guest',
  B2B2C: 'b2b2c',
  B2B_HR: 'B2B_HR',
} as const

const getUserCategory = (isInBusinessPage) => (isInBusinessPage ? USER_TYPE.BUSINESS : USER_TYPE.B2C)

const getUserSubCategory = ({ isInBusinessPage, isGuest, isB2B2C, currentUser }) => {
  if (isInBusinessPage) {
    if (isGuest) return BUSINESS_TYPE.GUEST
    if (isB2B2C) return BUSINESS_TYPE.B2B2C
    return BUSINESS_TYPE.B2B_HR
  }

  if (isGuest) return B2C_TYPE.GUEST
  if (isWithIn24HoursUser(currentUser)) return B2C_TYPE.WITHIN_24
  return B2C_TYPE.AFTER_24
}

const getCurrentHeroData = ({ heroData, currentUserType, isAITutorTab, isMobile }) => {
  const selectedHeroData = heroData?.find((hero) => hero.type === currentUserType)
  const heroSet = isAITutorTab ? selectedHeroData?.aiTutor : selectedHeroData?.combinedWithOneOnOne
  return heroSet?.find((hero) => hero.device === (isMobile ? 'mobile' : 'desktop'))
}

const getImageByLocale = (image, locale) => {
  const fallbackLocales = ['en', 'ko']
  const imageToUse = image[locale] || fallbackLocales.find((fallback) => image[fallback])
  return buildSanityImage(imageToUse)?.url()
}

const usePricingHero = ({ isAITutorTab = false }) => {
  const router = useAppRouter()
  const isMobile = useIsMobile()
  const { currentUser } = useCurrentUser()
  const { locale } = useTranslation()

  const { data: heroData } = usePricingHeroData()
  const { data: customerTypeData } = useCustomerType({
    enabled: !!currentUser,
  })

  const isGuest = !currentUser
  const isInBusinessPage = router.pathname.includes('business') || router.query.activity === 'BusinessPricingActivity'
  const isB2B2C = customerTypeData?.isB2CEligible || isGuest

  const userCategory = getUserCategory(isInBusinessPage)
  const userSubCategory = getUserSubCategory({ isInBusinessPage, isGuest, isB2B2C, currentUser })
  const currentUserType = `${userCategory}-${userSubCategory}`

  const currentHeroData = getCurrentHeroData({ heroData, currentUserType, isAITutorTab, isMobile })

  if (!currentHeroData) return null

  const { backgroundColor, image } = currentHeroData

  const pricingHero = {
    bgColor: backgroundColor,
    image: getImageByLocale(image, locale),
  }

  return pricingHero
}

export default usePricingHero
